import { useCallback, useEffect, useState } from 'react';
import { Download, FileSignature } from 'lucide-react';
import { api, download, post } from '../lib/api.js';
import { errorMessage } from '../lib/passkey.js';
import { Alert, fmtDate, Spinner } from '../lib/ui.js';
import { Badge } from '../lib/components.js';
import { formatRupees } from '@siva/shared';
import { t } from '../i18n/index.js';

type Draft = {
  period: string;
  periodLabel: string;
  fee: { monthlyRupees: number; perDayPaise: number; tdsRateBp: number; tdsSection: string };
  articles: { n: number; title: string }[];
};
type Issued = {
  id: string;
  number: string;
  version: number;
  published_at: string;
  superseded: boolean;
};
type Body = {
  draft: Draft | null;
  problem: { code: string; detail: string } | null;
  documents: Issued[];
};

/**
 * The service agreement for one worker.
 *
 * Issuing is deliberately a separate, deliberate act rather than something that happens when a
 * worker is created: it states a fee and a start date, and it is a contract. Re-issuing produces a
 * new version and supersedes the old one — both are kept, and both remain downloadable, because
 * "which one did they sign" is a question somebody will eventually ask.
 */
export function AgreementCard({ workerId }: { workerId: string }) {
  const [body, setBody] = useState<Body | null>(null);
  const [busy, setBusy] = useState(false);
  const [showOld, setShowOld] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setBody(await api<Body>(`/admin/workers/${workerId}/agreement`));
      setError(null);
    } catch (err) {
      setError(errorMessage(err));
    }
  }, [workerId]);
  useEffect(() => {
    void load();
  }, [load]);

  const issue = async () => {
    setBusy(true);
    setError(null);
    setNote(null);
    try {
      const r = await post<{ document: { number: string }; replaced: string | null }>(
        '/admin/agreements/publish',
        { user_id: workerId },
      );
      setNote(
        r.replaced
          ? t('agr_issued_replacing', { number: r.document.number, old: r.replaced })
          : t('agr_issued', { number: r.document.number }),
      );
      await load();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const get = async (d: Issued) => {
    try {
      await download(`/admin/documents/${d.id}.pdf`, `${d.number.replace(/\//g, '-')}.pdf`);
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  if (!body) return <Spinner />;
  const current = body.documents.find((d) => !d.superseded) ?? null;

  // What is in force, and then everything it replaced — behind a disclosure rather than in the
  // list. The owner opened a worker who had six agreements and saw five replaced ones filling the
  // card under the one that matters. They are not deleted: a published document is permanent and
  // somebody may be holding a printed copy of any of them, so each stays downloadable. They are
  // just no longer the first thing the page says.
  const replaced = body.documents.filter((d) => d.superseded);
  const shown = showOld ? body.documents : body.documents.filter((d) => !d.superseded);

  return (
    <section className="card">
      <div className="row between">
        <h3>
          <FileSignature size={16} aria-hidden="true" /> {t('agr_title')}
        </h3>
        {body.draft && (
          <button type="button" className="small secondary" disabled={busy} onClick={() => void issue()}>
            {busy ? t('agr_issuing') : current ? t('agr_reissue') : t('agr_issue')}
          </button>
        )}
      </div>
      <p className="muted">{t('agr_sub')}</p>
      {error && <Alert>{error}</Alert>}
      {note && <Alert kind="success">{note}</Alert>}
      {body.problem && <Alert kind="warn">{body.problem.detail}</Alert>}

      {body.draft && (
        <dl>
          <dt>{t('agr_effective')}</dt>
          <dd>{fmtDate(body.draft.period, false)}</dd>
          <dt>{t('agr_fee')}</dt>
          <dd>
            {formatRupees(body.draft.fee.monthlyRupees)} {t('agr_per_month')}
          </dd>
          <dt>{t('agr_tds')}</dt>
          <dd>
            {(body.draft.fee.tdsRateBp / 100).toFixed(
              body.draft.fee.tdsRateBp % 100 === 0 ? 0 : 2,
            )}
            % u/s {body.draft.fee.tdsSection}
          </dd>
        </dl>
      )}

      {body.documents.length === 0 ? (
        <p className="muted">{t('agr_none')}</p>
      ) : (
        <ul className="list">
          {shown.map((d) => (
            <li key={d.id} className="row between">
              <span>
                <strong>{d.number}</strong>{' '}
                {d.superseded ? (
                  <Badge tone="neutral" className="no-ml">
                    {t('agr_superseded')}
                  </Badge>
                ) : (
                  <Badge tone="green" className="no-ml">
                    {t('agr_current')}
                  </Badge>
                )}
                <br />
                <small className="muted">
                  {t('agr_issued_on', { date: fmtDate(d.published_at, false) })}
                </small>
              </span>
              <button type="button" className="small ghost" onClick={() => void get(d)}>
                <Download size={16} aria-hidden="true" /> {t('agr_pdf')}
              </button>
            </li>
          ))}
        </ul>
      )}

      {replaced.length > 0 && (
        <button
          type="button"
          className="small ghost"
          aria-expanded={showOld}
          onClick={() => setShowOld((v) => !v)}
        >
          {showOld ? t('agr_hide_old') : t('agr_show_old', { n: replaced.length })}
        </button>
      )}

    </section>
  );
}
